"use client";

import { useCallback, useRef, useState } from "react";
import { markSpotifyForceReauth } from "@/lib/spotifyAuth";
import { useStore } from "@/store";
import { resolveSessionMediaProvider } from "@/lib/adminSession";
import { reportProviderClientIssue } from "@/lib/providerClientIssue";
import {
  fetchSpotifyAvailableDevices,
  fetchSpotifyRemotePlayerState,
  getUsableSpotifyDevices,
  resumeSpotifyPlaybackOnDevice,
} from "./spotifyWebPlayer.client";
import { useSpotifyPlayerControls } from "./spotifyWebPlayer.controls";
import { useSpotifyPlayerEffects } from "./spotifyWebPlayer.effects";
import { initializeSpotifyPlayer } from "./spotifyWebPlayer.runtime";
import type { BaseWebPlayerController } from "./webPlayerContract";
import {
  describeSpotifyPlayer,
  disconnectSpotifyPlayer,
  isMissingSpotifyDeviceResponse,
  isUsableSpotifyPlayer,
  normalizeSpotifyDeviceName,
  spotifySharedStore,
  SPOTIFY_WEB_PLAYER_NAME,
  toSpotifyTrackMeta,
  wait,
  type SpotifyPlayerErrorOptions,
  type SpotifyPlayerLike,
  type SpotifySharedState,
} from "./spotifyWebPlayer.shared";

export type SpotifyWebPlayerOptions = {
  autoTransfer?: boolean;
  autoTransferPlay?: boolean;
};

export type SpotifyWebPlayerController = BaseWebPlayerController & {
  activateElement: () => Promise<void>;
  playSegment: (
    trackUri: string,
    startMs: number,
    durationMs?: number
  ) => Promise<void>;
};

export function useSpotifyWebPlayer(
  options: SpotifyWebPlayerOptions = {}
): SpotifyWebPlayerController {
  const authStatus = useStore.use.authStatus();
  const userProvider = useStore.use.user()?.provider ?? null;
  const provider = resolveSessionMediaProvider(userProvider, null, "participant");
  const spotifyEnabled = authStatus === "authed" && provider === "spotify";
  const { autoTransfer = true, autoTransferPlay = false } = options;
  const initialSharedState = spotifySharedStore.getState();
  const playerRef = useRef<SpotifyPlayerLike | null>(initialSharedState.player);

  const [isReady, setIsReady] = useState(initialSharedState.isReady);
  const [isPlaying, setIsPlaying] = useState(initialSharedState.isPlaying);
  const [deviceId, setDeviceId] = useState<string | null>(
    initialSharedState.deviceId
  );
  const [currentTrack, setCurrentTrack] = useState(initialSharedState.currentTrack);
  const [positionMs, setPositionMs] = useState(initialSharedState.positionMs);
  const [durationMs, setDurationMs] = useState(initialSharedState.durationMs);
  const [error, setError] = useState<string | null>(initialSharedState.error);
  const [volume, setVolumeState] = useState(initialSharedState.volume);
  const [needsTransfer, setNeedsTransfer] = useState(
    initialSharedState.needsTransfer
  );
  const [playerInitTick, setPlayerInitTick] = useState(0);

  const patchSharedState = useCallback((patch: Partial<SpotifySharedState>) => {
    spotifySharedStore.patchState(patch);
  }, []);

  const syncStateFromShared = useCallback(() => {
    const sharedState = spotifySharedStore.getState();
    setDeviceId(sharedState.deviceId);
    setIsReady(sharedState.isReady);
    setIsPlaying(sharedState.isPlaying);
    setCurrentTrack(sharedState.currentTrack);
    setPositionMs(sharedState.positionMs);
    setDurationMs(sharedState.durationMs);
    setError(sharedState.error);
    setVolumeState(sharedState.volume);
    setNeedsTransfer(sharedState.needsTransfer);
  }, []);

  const setPlayerError = useCallback(
    (message: string, options: SpotifyPlayerErrorOptions = {}) => {
      const sharedState = spotifySharedStore.getState();
      patchSharedState({
        error: message,
      });
      const category = options.category ?? "playback";
      reportProviderClientIssue({
        provider: "spotify",
        category,
        level: options.level ?? "error",
        event: options.event ?? options.reason ?? "player_error",
        title:
          options.title ??
          (category === "auth"
            ? "Spotify authorization blocked"
            : "Spotify playback blocked"),
        message,
        error: options.error,
        details: {
          reason: options.reason ?? null,
          markForReauth: Boolean(options.markForReauth),
          deviceId: sharedState.deviceId,
          isReady: sharedState.isReady,
          needsTransfer: sharedState.needsTransfer,
          hasPlaybackState: sharedState.hasPlaybackState,
          ...(options.details ?? {}),
        },
      });
      if (options.markForReauth) {
        markSpotifyForceReauth(options.reason || "player_error", message);
      }
    },
    [patchSharedState]
  );

  const markDeviceUnavailable = useCallback(() => {
    patchSharedState({
      deviceId: null,
      isReady: false,
      isPlaying: false,
      needsTransfer: true,
      hasPlaybackState: false,
      error: null,
    });
  }, [patchSharedState]);

  const resetPlayerInstance = useCallback(
    (reason: string, details?: Record<string, unknown>) => {
      void reason;
      void details;
      const sharedState = spotifySharedStore.getState();
      disconnectSpotifyPlayer(sharedState.player ?? playerRef.current);
      playerRef.current = null;
      patchSharedState({
        player: null,
        deviceId: null,
        isReady: false,
        isPlaying: false,
        currentTrack: null,
        positionMs: 0,
        durationMs: 0,
        needsTransfer: false,
        hasPlaybackState: false,
        error: null,
      });
      setPlayerInitTick((tick) => tick + 1);
    },
    [patchSharedState]
  );

  const getPlayerOrReset = useCallback(
    (reason: string) => {
      const player = spotifySharedStore.getState().player ?? playerRef.current;
      if (!player) return null;
      if (isUsableSpotifyPlayer(player)) {
        if (playerRef.current !== player) {
          playerRef.current = player;
        }
        if (spotifySharedStore.getState().player !== player) {
          patchSharedState({
            player,
          });
        }
        return player;
      }

      resetPlayerInstance(reason, {
        playerShape: describeSpotifyPlayer(player),
      });
      return null;
    },
    [patchSharedState, resetPlayerInstance]
  );

  const hydrateSharedStateFromLocalPlayback = useCallback(
    async (reason: string) => {
      const player = getPlayerOrReset(reason);
      if (!player) {
        return null;
      }

      try {
        const state = await player.getCurrentState();
        if (!state) return null;

        const current = state.track_window?.current_track;
        patchSharedState({
          isPlaying: !state.paused,
          positionMs: state.position,
          durationMs: state.duration,
          isReady: true,
          needsTransfer: false,
          hasPlaybackState: true,
          error: null,
          currentTrack: current
            ? toSpotifyTrackMeta(current)
            : spotifySharedStore.getState().currentTrack,
        });
        return state;
      } catch {
        return null;
      }
    },
    [getPlayerOrReset, patchSharedState]
  );

  const waitForLocalPlaybackActivation = useCallback(
    async (timeoutMs = 2200) => {
      const start = Date.now();
      do {
        const state = await hydrateSharedStateFromLocalPlayback(
          "waitForLocalPlaybackActivation invalid Spotify player"
        );
        if (state) {
          return {
            active: true,
            isPlaying: !state.paused,
          };
        }
        await wait(140);
      } while (Date.now() - start < timeoutMs);

      return {
        active: false,
        isPlaying: false,
      };
    },
    [hydrateSharedStateFromLocalPlayback]
  );

  const fetchRemotePlayerState = useCallback(async () => {
    return await fetchSpotifyRemotePlayerState();
  }, []);
  const fetchAvailableDevices = useCallback(async () => {
    return await fetchSpotifyAvailableDevices();
  }, []);

  const resolveTransferDeviceId = useCallback(
    async (preferredDeviceId: string | null) => {
      const devices = await fetchAvailableDevices();
      if (!devices) {
        return preferredDeviceId;
      }

      const usableDevices = getUsableSpotifyDevices(devices);
      const preferredMatch =
        preferredDeviceId
          ? usableDevices.find((device) => device.id === preferredDeviceId)
          : null;
      const namedMatch = usableDevices.find(
        (device) =>
          normalizeSpotifyDeviceName(device.name) ===
          normalizeSpotifyDeviceName(SPOTIFY_WEB_PLAYER_NAME)
      );
      const resolvedDeviceId =
        preferredMatch?.id ?? namedMatch?.id ?? preferredDeviceId ?? null;

      if (resolvedDeviceId) {
        patchSharedState({
          deviceId: resolvedDeviceId,
          isReady: true,
          needsTransfer: !(preferredMatch?.isActive ?? namedMatch?.isActive ?? false),
          error: null,
        });
      }

      return resolvedDeviceId;
    },
    [fetchAvailableDevices, patchSharedState]
  );

  const resumePlaybackOnDevice = useCallback(
    async (playbackDeviceId: string) => {
      const result = await resumeSpotifyPlaybackOnDevice(playbackDeviceId);
      if (result.ok) return true;

      if (isMissingSpotifyDeviceResponse(result.status, result.responseText)) {
        markDeviceUnavailable();
        return false;
      }

      setPlayerError(result.errorMessage ?? "Failed to resume playback", {
        event: "resume_playback_failed",
        details: {
          status: result.status,
          playbackDeviceId,
        },
      });
      return false;
    },
    [markDeviceUnavailable, setPlayerError]
  );

  const initPlayer = useCallback(() => {
    initializeSpotifyPlayer({
      patchSharedState,
      resetPlayerInstance,
      setPlayerError,
      syncStateFromShared,
      playerRef,
    });
  }, [patchSharedState, resetPlayerInstance, setPlayerError, syncStateFromShared]);

  const {
    togglePlay,
    nextTrack,
    prevTrack,
    seek,
    setVolume,
    stopPlayback,
    activateElement,
    ensureReady,
    resumePlaybackHere,
    playSegment,
  } = useSpotifyPlayerControls({
    spotifyEnabled,
    deviceId,
    getPlayerOrReset,
    patchSharedState,
    setVolumeState,
    initPlayer,
    markDeviceUnavailable,
    resolveTransferDeviceId,
    waitForLocalPlaybackActivation,
    resumePlaybackOnDevice,
    fetchRemotePlayerState,
    setPlayerError,
  });

  useSpotifyPlayerEffects({
    spotifyEnabled,
    isReady,
    deviceId,
    autoTransfer,
    autoTransferPlay,
    playerInitTick,
    playerRef,
    patchSharedState,
    syncStateFromShared,
    getPlayerOrReset,
    initPlayer,
    stopPlayback,
    resumePlaybackHere,
  });

  return {
    isReady,
    isPlaying,
    sharedCurrentTrack: currentTrack,
    deviceId,
    positionMs,
    durationMs,
    volume,
    error,
    needsTransfer,

    togglePlay,
    nextTrack,
    prevTrack,
    seek,
    setVolume,
    stopPlayback,
    activateElement,
    ensureReady,
    resumePlaybackHere,
    playSegment,
  };
}
